import React, { useCallback, useState } from 'react';
import {
  TableRow,
  TableCell,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Autocomplete
} from '@mui/material';
import { Delete, InfoOutlined } from '@mui/icons-material';
import { HHMM_LIMIT_HELPER_TEXT } from '../../constants/hhmmHoursLimits';
import {
  formatHoursValue,
  validateHoursInput,
  elapsedIsCalendarBased,
  formatTsrCalendarDueRangeDisplay,
  getCalendarDueRangeMuiColor,
  TSR_TIME_UNITS,
  isDecimalNumericTsrUnit
} from './componentFormUtils';
import { recordUsesCyclesUnit } from '../../utils/tlbCycleCounters';

// Time Since Record Row Component to avoid arrow functions in JSX
const TimeSinceRecordRow = React.memo(({
  record,
  index,
  onUpdate,
  onRemove,
  validateYearsInput,
  validateCyclesInput,
  validateCalendarDaysInput,
  dueAtParentAssembly,
  dueAtAirframeAssembly,
  dueAtThisItself,
  readOnly = false,
  labelOptions = [],
  tlbCycleOptions = [],
  tlbCycleOptionsLoading = false,
  onSelectTlbCycle
}) => {
  // State to track validation errors
  const [limitError, setLimitError] = useState('');
  const [elapsedError, setElapsedError] = useState('');
  const sanitizeLandingInput = useCallback((value) => {
    if (!value) return '';
    const digitsOnly = value.replace(/[^\d]/g, '');
    return digitsOnly.length > 8 ? digitsOnly.substring(0, 8) : digitsOnly;
  }, []);
  const usesCycles = recordUsesCyclesUnit(record);
  const selectedCycleOption =
    usesCycles && record.tlbCarriedForwardKey
      ? (tlbCycleOptions || []).find((o) => o.key === record.tlbCarriedForwardKey)
      : null;

  const normalizedOptions = Array.isArray(labelOptions)
    ? labelOptions
        .map(opt => (opt || '').trim().toLowerCase())
        .filter(Boolean)
    : [];
  const currentLabel = (record.label || '').trim();
  const isUnmappedLabel =
    !!currentLabel &&
    normalizedOptions.length > 0 &&
    !normalizedOptions.includes(currentLabel.toLowerCase());
  const handleTypeChange = useCallback((e) => {
    onUpdate(index, 'type', e.target.value);
  }, [index, onUpdate]);

  const handleLabelChange = useCallback((e) => {
    onUpdate(index, 'label', e.target.value);
  }, [index, onUpdate]);

  const handleLimitValueChange = useCallback((e) => {
    let validatedValue = e.target.value;
    let error = '';
    
    if (record.limit?.unit === 'Hours') {
      const validation = validateHoursInput(e.target.value);
      validatedValue = validation.value;
      error = validation.error;
      setLimitError(error);
    } else if (record.limit?.unit === 'Years') {
      validatedValue = validateYearsInput(e.target.value);
      setLimitError('');
    } else if (isDecimalNumericTsrUnit(record.limit?.unit) && validateCyclesInput) {
      validatedValue = validateCyclesInput(e.target.value);
      setLimitError('');
    } else if (record.limit?.unit === 'Landings') {
      validatedValue = sanitizeLandingInput(e.target.value);
      setLimitError('');
    } else if (record.limit?.unit === 'Calendar Days' && validateCalendarDaysInput) {
      validatedValue = validateCalendarDaysInput(e.target.value);
      setLimitError('');
    } else {
      setLimitError('');
    }
    
    onUpdate(index, 'limit.value', validatedValue);
  }, [index, onUpdate, record.limit?.unit, validateYearsInput, validateCyclesInput, validateCalendarDaysInput, sanitizeLandingInput]);

  const handleLimitValueBlur = useCallback((e) => {
    // Format Hours value to ensure colon is present
    if (record.limit?.unit === 'Hours' && e.target.value) {
      const formattedValue = formatHoursValue(e.target.value);
      if (formattedValue !== e.target.value) {
    onUpdate(index, 'limit.value', formattedValue);
        // Clear error after formatting
        setLimitError('');
      }
    }
  }, [index, onUpdate, record.limit?.unit]);

  const handleLimitUnitChange = useCallback((e) => {
    const newUnit = e.target.value;
    const oldLimitUnit = record.limit?.unit ?? 'Hours';
    const elapsedUnitBefore = record.elapsedTime?.unit ?? 'Hours';
    onUpdate(index, 'limit.unit', newUnit);
    // Only sync elapsed (and installed snapshot unit) when it matched the previous limit — keeps Years↔Calendar Days mixed rows intact
    if (elapsedUnitBefore === oldLimitUnit) {
      onUpdate(index, 'elapsedTime.unit', newUnit);
      onUpdate(index, 'installedValue.unit', newUnit);
    } else {
      onUpdate(index, 'installedValue.unit', elapsedUnitBefore);
    }
  }, [index, onUpdate, record.limit?.unit, record.elapsedTime?.unit]);

  const handleElapsedValueChange = useCallback((e) => {
    const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
    let validatedValue = e.target.value;
    let error = '';
    
    if (unit === 'Hours') {
      const validation = validateHoursInput(e.target.value);
      validatedValue = validation.value;
      error = validation.error;
      setElapsedError(error);
    } else if (unit === 'Years') {
      validatedValue = validateYearsInput(e.target.value);
      setElapsedError('');
    } else if (isDecimalNumericTsrUnit(unit) && validateCyclesInput) {
      validatedValue = validateCyclesInput(e.target.value);
      setElapsedError('');
    } else if (unit === 'Landings') {
      validatedValue = sanitizeLandingInput(e.target.value);
      setElapsedError('');
    } else if (unit === 'Calendar Days' && validateCalendarDaysInput) {
      validatedValue = validateCalendarDaysInput(e.target.value);
      setElapsedError('');
    } else {
      setElapsedError('');
    }
    
    onUpdate(index, 'elapsedTime.value', validatedValue);
  }, [index, onUpdate, record.limit?.unit, record.elapsedTime?.unit, validateYearsInput, validateCyclesInput, validateCalendarDaysInput, sanitizeLandingInput]);

  const handleElapsedValueBlur = useCallback((e) => {
    // Format Hours value to ensure colon is present
    if (record.elapsedTime?.unit === 'Hours' && e.target.value) {
      const formattedValue = formatHoursValue(e.target.value);
      if (formattedValue !== e.target.value) {
    onUpdate(index, 'elapsedTime.value', formattedValue);
        // Clear error after formatting
        setElapsedError('');
      }
    }
  }, [index, onUpdate, record.elapsedTime?.unit]);

  const handleElapsedUnitChange = useCallback((e) => {
    onUpdate(index, 'elapsedTime.unit', e.target.value);
  }, [index, onUpdate]);

  const handleRemainingValueChange = useCallback((e) => {
    onUpdate(index, 'remainingTime.value', e.target.value);
  }, [index, onUpdate]);

  const handleStartDateChange = useCallback((e) => {
    onUpdate(index, 'startDate', e.target.value);
  }, [index, onUpdate]);

  const handleAsOnDateChange = useCallback((e) => {
    onUpdate(index, 'asOnDate', e.target.value);
  }, [index, onUpdate]);

  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  const disableAirframeParentInstallFields =
    readOnly || elapsedIsCalendarBased(record.elapsedTime?.unit);

  const calendarDueRangeLabel = formatTsrCalendarDueRangeDisplay(record);
  const calendarDueColor = calendarDueRangeLabel
    ? getCalendarDueRangeMuiColor(record)
    : 'text.secondary';

  return (
    <TableRow>
      <TableCell>
        <FormControl size="small" fullWidth>
          <Select
            value={record.type}
            onChange={handleTypeChange}
            disabled={readOnly}
            sx={{ fontSize: '0.75rem' }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 400,
                  minWidth: 300,
                  '& .MuiMenuItem-root': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    padding: '8px 12px',
                    minHeight: 'auto',
                    lineHeight: 1.4,
                    overflow: 'visible',
                    color: '#000',
                    fontSize: '0.75rem',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
          >
            <MenuItem value="TSN" sx={{ fontSize: '0.75rem' }}>TSN</MenuItem>
            <MenuItem value="TSO" sx={{ fontSize: '0.75rem' }}>TSO</MenuItem>
            <MenuItem value="TSI" sx={{ fontSize: '0.75rem' }}>TSI</MenuItem>
            <MenuItem value="TSP" sx={{ fontSize: '0.75rem' }}>TSP</MenuItem>
            <MenuItem value="TSR" sx={{ fontSize: '0.75rem' }}>TSR</MenuItem>
            <MenuItem value="SLL" sx={{ fontSize: '0.75rem' }}>SLL</MenuItem>
            <MenuItem value="OTL" sx={{ fontSize: '0.75rem' }}>OTL</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell sx={{ minWidth: 220 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ flex: 1 }}>
            {usesCycles && Array.isArray(tlbCycleOptions) && tlbCycleOptions.length > 0 ? (
              <FormControl size="small" fullWidth>
                <Select
                  displayEmpty
                  value={record.tlbCarriedForwardKey || ''}
                  onChange={(e) => {
                    if (readOnly || !onSelectTlbCycle) return;
                    const key = e.target.value;
                    const opt = tlbCycleOptions.find((o) => o.key === key);
                    if (opt) onSelectTlbCycle(index, opt);
                  }}
                  disabled={readOnly || tlbCycleOptionsLoading}
                  sx={{ fontSize: '0.75rem' }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {tlbCycleOptionsLoading ? 'Loading TLB counters…' : 'Select TLB cycle counter'}
                        </Typography>
                      );
                    }
                    const opt = tlbCycleOptions.find((o) => o.key === selected);
                    if (!opt) return selected;
                    return `${opt.label} — ${opt.liveDisplay}`;
                  }}
                >
                  <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>
                    <em>Select TLB cycle counter</em>
                  </MenuItem>
                  {tlbCycleOptions.map((opt) => (
                    <MenuItem key={opt.key} value={opt.key} sx={{ fontSize: '0.75rem' }}>
                      {opt.label} — {opt.liveDisplay}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : Array.isArray(labelOptions) && labelOptions.length > 0 ? (
              <Autocomplete
                size="small"
                freeSolo
                disableClearable={false}
                options={labelOptions}
                value={record.label || ''}
                readOnly={readOnly}
                onChange={(_, newValue) => {
                  if (!readOnly) {
                    onUpdate(index, 'label', newValue || '');
                  }
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (!readOnly && reason === 'input') {
                    onUpdate(index, 'label', newInputValue || '');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      readOnly
                    }}
                    sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                  />
                )}
              />
            ) : (
              <TextField
                size="small"
                fullWidth
                value={record.label}
                onChange={handleLabelChange}
                InputProps={{ readOnly }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
              />
            )}
          </Box>
          {isUnmappedLabel && !usesCycles && (
            <Tooltip title="This label does not match the current master/custom configuration for any relevant assembly type, but will be saved as-is.">
              <InfoOutlined sx={{ fontSize: '1rem' }} color="warning" />
            </Tooltip>
          )}
        </Box>
        {usesCycles && selectedCycleOption && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            TLB live (carried): {selectedCycleOption.liveDisplay}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Grid container spacing={1}>
          <Grid item xs={7}>
            <TextField
              size="small"
              fullWidth
              type={record.limit?.unit === 'Hours' ? 'text' : 'text'}
              value={record.limit?.value || ''}
              onChange={handleLimitValueChange}
              onBlur={handleLimitValueBlur}
              placeholder={record.limit?.unit === 'Hours' ? 'HH:MM' : record.limit?.unit === 'Years' ? 'YY.MM (e.g., 2.10)' : '0'}
              helperText={
                record.limit?.unit === 'Hours'
                  ? limitError || HHMM_LIMIT_HELPER_TEXT
                  : record.limit?.unit === 'Years'
                  ? 'Format: YY.MM (years.months, max 2 digits for years, 0-12 for months)'
                  : (record.limit?.unit === 'Calendar Days' || record.limit?.unit === 'Landings')
                  ? 'Numeric format (integer)'
                  : isDecimalNumericTsrUnit(record.limit?.unit)
                  ? 'Numeric format (up to 2 decimal places)'
                  : ''
              }
              error={record.limit?.unit === 'Hours' && !!limitError}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
            />
          </Grid>
          <Grid item xs={5}>
            <FormControl size="small" fullWidth>
              <Select
                value={record.limit?.unit || 'Hours'}
                onChange={handleLimitUnitChange}
                disabled={readOnly}
                sx={{ fontSize: '0.75rem' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      minWidth: 200,
                      '& .MuiMenuItem-root': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        padding: '8px 12px',
                        minHeight: 'auto',
                        lineHeight: 1.4,
                        overflow: 'visible',
                        color: '#000',
                        fontSize: '0.75rem',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        }
                      }
                    }
                  }
                }}
              >
                {TSR_TIME_UNITS.map((unit) => (
                  <MenuItem key={unit} value={unit} sx={{ fontSize: '0.75rem' }}>{unit}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </TableCell>
      {/* Elapsed value of THIS Component itself at Installation */}
      <TableCell>
        <Grid container spacing={1}>
          <Grid item xs={7}>
            <Tooltip
              title={(() => {
                const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
                return `Enter elapsed value of THIS component itself at installation (${unit}).`;
              })()}
              placement="top"
              arrow
            >
              <TextField
                size="small"
                fullWidth
                type="text"
                value={
                  record.installedValue?.value != null && String(record.installedValue.value).trim() !== ''
                    ? record.installedValue.value
                    : (record.elapsedTime?.value || '')
                }
                onChange={handleElapsedValueChange}
                onBlur={handleElapsedValueBlur}
                placeholder={(() => {
                  const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
                  return unit === 'Hours'
                    ? 'HH:MM'
                    : unit === 'Years'
                    ? 'YY.MM (e.g., 2.10)'
                    : '0';
                })()}
                helperText={(() => {
                  const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
                  if (unit === 'Hours') {
                    return elapsedError || HHMM_LIMIT_HELPER_TEXT;
                  }
                  if (unit === 'Years') {
                    return (record.startDate && record.asOnDate 
                      ? 'Auto-calculated from dates (format: YY.MM, can be overridden)' 
                      : 'Format: YY.MM (years.months, max 2 digits for years, 0-12 for months)');
                  }
                  if (unit === 'Calendar Days' || unit === 'Landings') {
                    return 'Numeric format (integer)';
                  }
                  if (isDecimalNumericTsrUnit(unit)) {
                    return 'Numeric format (up to 2 decimal places)';
                  }
                  return '';
                })()}
                error={(record.limit?.unit || record.elapsedTime?.unit || 'Hours') === 'Hours' && !!elapsedError}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={5}>
            <FormControl size="small" fullWidth>
              <Select
                value={record.elapsedTime?.unit || 'Hours'}
                onChange={handleElapsedUnitChange}
                disabled={readOnly}
                sx={{ fontSize: '0.75rem' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      minWidth: 200,
                      '& .MuiMenuItem-root': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        padding: '8px 12px',
                        minHeight: 'auto',
                        lineHeight: 1.4,
                        overflow: 'visible',
                        color: '#000',
                        fontSize: '0.75rem',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        }
                      }
                    }
                  }
                }}
              >
                {TSR_TIME_UNITS.map((unit) => (
                  <MenuItem key={unit} value={unit} sx={{ fontSize: '0.75rem' }}>{unit}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </TableCell>

      {/* Airframe Value at Installation */}
      <TableCell>
        <Grid container spacing={1}>
          <Grid item xs={7}>
            <Tooltip
              title={(() => {
                if (disableAirframeParentInstallFields && !readOnly) {
                  return 'Not used when Elapsed is Years or Calendar Days (elapsed comes from Start / As On dates).';
                }
                const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
                return `Enter Airframe value at the moment this component was installed (${unit}).`;
              })()}
              placement="top"
              arrow
            >
              <TextField
                size="small"
                fullWidth
                type="text"
                value={record.airframeValueAtInstallation?.value || ''}
                disabled={disableAirframeParentInstallFields}
                onChange={(e) => {
                  const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
                  let validatedValue = e.target.value;
                  if (unit === 'Hours') {
                    const validation = validateHoursInput(e.target.value);
                    validatedValue = validation.value;
                  } else if (unit === 'Years') {
                    validatedValue = validateYearsInput(e.target.value);
                  } else if (unit === 'Landings') {
                    validatedValue = sanitizeLandingInput(e.target.value);
                  } else if (unit === 'Calendar Days') {
                    validatedValue = validateCalendarDaysInput
                      ? validateCalendarDaysInput(e.target.value)
                      : e.target.value;
                  } else if (isDecimalNumericTsrUnit(unit) && validateCyclesInput) {
                    validatedValue = validateCyclesInput(e.target.value);
                  }
                  onUpdate(index, 'airframeValueAtInstallation.value', validatedValue);
                }}
                placeholder={(() => {
                  const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
                  return unit === 'Hours'
                    ? 'HH:MM'
                    : unit === 'Years'
                    ? 'YY.MM (e.g., 2.10)'
                    : '0';
                })()}
                helperText={(() => {
                  const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
                  if (unit === 'Hours') {
                    return HHMM_LIMIT_HELPER_TEXT;
                  }
                  if (unit === 'Years') {
                    return 'Format: YY.MM (years.months, max 2 digits for years, 0-12 for months)';
                  }
                  if (unit === 'Calendar Days' || unit === 'Landings') {
                    return 'Numeric format (integer)';
                  }
                  if (isDecimalNumericTsrUnit(unit)) {
                    return 'Numeric format (up to 2 decimal places)';
                  }
                  return '';
                })()}
                sx={{
                  '& .MuiInputBase-input': { fontSize: '0.75rem' },
                  ...(disableAirframeParentInstallFields && {
                    '& .MuiInputBase-input': { backgroundColor: '#f5f5f5', cursor: 'not-allowed' }
                  })
                }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={5}>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', py: 1, color: 'text.secondary' }}>
              {record.limit?.unit || record.elapsedTime?.unit || 'Hours'}
            </Typography>
          </Grid>
        </Grid>
      </TableCell>

      {/* Parent Assembly's Value at Installation */}
      <TableCell>
        <Grid container spacing={1}>
          <Grid item xs={7}>
            <Tooltip
              title={(() => {
                if (disableAirframeParentInstallFields && !readOnly) {
                  return 'Not used when Elapsed is Years or Calendar Days (elapsed comes from Start / As On dates).';
                }
                const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
                return `Enter Parent Assembly's value at the moment this component was installed (${unit}).`;
              })()}
              placement="top"
              arrow
            >
              <TextField
                size="small"
                fullWidth
                type="text"
                value={record.parentAssemblyValueAtInstallation?.value || ''}
                disabled={disableAirframeParentInstallFields}
                onChange={(e) => {
                  const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
                  let validatedValue = e.target.value;
                  if (unit === 'Hours') {
                    const validation = validateHoursInput(e.target.value);
                    validatedValue = validation.value;
                  } else if (unit === 'Years') {
                    validatedValue = validateYearsInput(e.target.value);
                  } else if (unit === 'Landings') {
                    validatedValue = sanitizeLandingInput(e.target.value);
                  } else if (unit === 'Calendar Days') {
                    validatedValue = validateCalendarDaysInput
                      ? validateCalendarDaysInput(e.target.value)
                      : e.target.value;
                  } else if (isDecimalNumericTsrUnit(unit) && validateCyclesInput) {
                    validatedValue = validateCyclesInput(e.target.value);
                  }
                  onUpdate(index, 'parentAssemblyValueAtInstallation.value', validatedValue);
                }}
                placeholder={(() => {
                  const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
                  return unit === 'Hours'
                    ? 'HH:MM'
                    : unit === 'Years'
                    ? 'YY.MM (e.g., 2.10)'
                    : '0';
                })()}
                helperText={(() => {
                  const unit = record.limit?.unit || record.elapsedTime?.unit || 'Hours';
                  if (unit === 'Hours') {
                    return HHMM_LIMIT_HELPER_TEXT;
                  }
                  if (unit === 'Years') {
                    return 'Format: YY.MM (years.months, max 2 digits for years, 0-12 for months)';
                  }
                  if (unit === 'Calendar Days' || unit === 'Landings') {
                    return 'Numeric format (integer)';
                  }
                  if (isDecimalNumericTsrUnit(unit)) {
                    return 'Numeric format (up to 2 decimal places)';
                  }
                  return '';
                })()}
                sx={{
                  '& .MuiInputBase-input': { fontSize: '0.75rem' },
                  ...(disableAirframeParentInstallFields && {
                    '& .MuiInputBase-input': { backgroundColor: '#f5f5f5', cursor: 'not-allowed' }
                  })
                }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={5}>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', py: 1, color: 'text.secondary' }}>
              {record.limit?.unit || record.elapsedTime?.unit || 'Hours'}
            </Typography>
          </Grid>
        </Grid>
      </TableCell>
      {/* Remaining */}
      <TableCell>
        <Grid container spacing={1}>
          <Grid item xs={7}>
            <TextField
              size="small"
              fullWidth
              type="text"
              value={record.remainingTime?.value || ''}
              onChange={handleRemainingValueChange}
              placeholder={record.remainingTime?.unit === 'Hours' ? 'HH:MM' : record.remainingTime?.unit === 'Years' ? 'YY.MM (e.g., 2.10)' : '0'}
              disabled
              sx={{ 
                '& .MuiInputBase-input': { 
                  backgroundColor: '#f5f5f5',
                  cursor: 'not-allowed',
                  fontSize: '0.75rem'
                } 
              }}
            />
          </Grid>
          <Grid item xs={5}>
            <FormControl size="small" fullWidth>
              <Select
                value={record.elapsedTime?.unit || 'Hours'}
                disabled
                title="Remaining uses the same unit as Elapsed (including when Limit uses a different calendar format)."
                sx={{ 
                  fontSize: '0.75rem',
                  '& .MuiSelect-select': { 
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  } 
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      minWidth: 200,
                      '& .MuiMenuItem-root': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        padding: '8px 12px',
                        minHeight: 'auto',
                        lineHeight: 1.4,
                        overflow: 'visible',
                        color: '#000',
                        fontSize: '0.75rem',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        }
                      }
                    }
                  }
                }}
              >
                {TSR_TIME_UNITS.map((unit) => (
                  <MenuItem key={unit} value={unit} sx={{ fontSize: '0.75rem' }}>{unit}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </TableCell>

      {/* Calendar due range (Years / Calendar Days only) */}
      <TableCell>
        <Tooltip
          title={
            calendarDueRangeLabel
              ? 'From: Start Date if set, otherwise As On Date. To: As On Date plus remaining (calendar). Red if due in ≤7 days or overdue; green otherwise.'
              : 'Shown when Elapsed/Remaining is Years or Calendar Days, with As On Date and remaining set.'
          }
          placement="top"
          arrow
        >
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: calendarDueColor, whiteSpace: 'normal' }}>
            {calendarDueRangeLabel || '—'}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Due At Airframe Assembly */}
      <TableCell>
        <Tooltip
          title={
            dueAtAirframeAssembly
              ? 'Due At Airframe Assembly: calculated from Airframe Value at Installation plus this component’s Remaining (or from airframe comparison when snapshot is not available).'
              : ''
          }
          placement="top"
          arrow
        >
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {dueAtAirframeAssembly || '—'}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Start Date */}
      <TableCell>
        <Box>
          <Tooltip
            title="Date when tracking for this TSR began (e.g., installation, overhaul)."
            placement="top"
            arrow
          >
            <TextField
              size="small"
              type="date"
              value={record.startDate ? new Date(record.startDate).toISOString().split('T')[0] : ''}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
            />
          </Tooltip>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block', mt: 0.5 }}>
            Date when tracking began (e.g., installation, overhaul)
          </Typography>
        </Box>
      </TableCell>

      {/* As On Date */}
      <TableCell>
        <Box>
          <Tooltip
            title="Effective date for this record (used to calculate elapsed/remaining, especially for calendar-based units)."
            placement="top"
            arrow
          >
            <TextField
              size="small"
              type="date"
              value={record.asOnDate ? new Date(record.asOnDate).toISOString().split('T')[0] : ''}
              onChange={handleAsOnDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
            />
          </Tooltip>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block', mt: 0.5 }}>
            Effective date for elapsed time calculation (auto-calculates for Years/Calendar Days)
          </Typography>
        </Box>
      </TableCell>

      {/* Due At Parent Assembly */}
      <TableCell>
        <Tooltip
          title={
            dueAtParentAssembly
              ? 'Due At Parent Assembly: calculated from Parent Assembly’s Value at Installation plus this component’s Remaining (or from parent comparison when snapshot is not available).'
              : ''
          }
          placement="top"
          arrow
        >
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {dueAtParentAssembly || '—'}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Due At This Itself */}
      <TableCell>
        <Tooltip
          title={
            dueAtThisItself
              ? 'Due At This Itself: if a Life Limit is set, it equals the Life Limit; otherwise it is the sum of the installation snapshot and Remaining.'
              : ''
          }
          placement="top"
          arrow
        >
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {dueAtThisItself || '—'}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <IconButton
          size="small"
          color="error"
          onClick={handleRemove}
        >
          <Delete />
        </IconButton>
      </TableCell>
    </TableRow>
  );
});

TimeSinceRecordRow.displayName = 'TimeSinceRecordRow';

export default TimeSinceRecordRow;
